'use client';

import { useCallback, useMemo, useRef } from 'react';
import { Cart, LineItem } from 'shared/types/cart';
import { OriginalPhotoInfo, getBuiltUrl, setupImageOptions } from 'helpers/imageData';
import { getCustomizationData, updateCustomizationData } from 'helpers/utils/customizationStorage';
import { useMessagePort } from 'providers/message-channel';
import { sdk } from 'sdk';
import { useCart, useProjectSettings } from 'frontastic';
import { lineItemHasPhoto, lineItemHasExpiredPhotos } from './helper';

// wait 10 seconds to get a response from the host site after sending the refreshPhotos message
const FILE_SELECTION_TIMEOUT_MS = 10000;

// Refresh images in a cart at most once every 15 minutes, unless the force flag is set to true
// This is to avoid all the different ways that refreshImages can be called (on interval, on focus,
// on visibility change, on first load) from calling refreshImages too often and causing unnecessary
// network requests and potential issues with the host site.
const REFRESH_CART_IMAGES_TTL_MS = 15 * 60 * 1000;

// When we update line items on the backend, if it fails we want to retry twice, with an exponential
// backoff delay starting at 250 ms
const UPDATE_LINE_ITEMS_MAX_RETRIES = 2;
const UPDATE_LINE_ITEMS_BASE_DELAY_MS = 250;

// key used in session storage to know when we last refreshed a cart's images
const REFRESH_CART_IMAGES_LAST_REFRESHED_AT_KEY_PREFIX = 'refresh-cart-image-auth:last-refreshed-at:';

let refreshPhotosPromise: Promise<OriginalPhotoInfo[]> | null = null;

type RefreshImagesForCartResult = {
  files: OriginalPhotoInfo[];
  updatedCarts: Cart[];
};

type ShouldRefreshCartImagesParams = {
  cartId: string | null | undefined;
  lineItems?: LineItem[];
  force?: boolean;
};

type RefreshImagesForOrdersOptions = {
  force?: boolean;
};

type RefreshImagesForOrdersResult = {
  files: OriginalPhotoInfo[];
  refreshedLineItems: LineItem[];
  refreshedPhotoIds: string[];
};

/**
 * @returns An array of unique photo IDs that are present in the provided carts.
 */
const getPreselectedPhotoIds = (carts: Cart[]) =>
  Array.from(
    new Set(
      carts.flatMap((cart) =>
        (cart.lineItems ?? []).filter(lineItemHasPhoto).map((lineItem) => lineItem.imageMetadata?.id as string),
      ),
    ),
  );

const getPreselectedPhotoIdsFromLineItems = (lineItems: LineItem[]) =>
  Array.from(new Set(lineItems.filter(lineItemHasPhoto).map((lineItem) => lineItem.imageMetadata?.id as string)));

const canUseSessionStorage = () => globalThis.window?.sessionStorage !== undefined;
const getStorageKey = (cartId: string) => `${REFRESH_CART_IMAGES_LAST_REFRESHED_AT_KEY_PREFIX}${cartId}`;

const getRefreshCartImagesLastRefreshedAt = (cartId: string) => {
  if (!canUseSessionStorage()) return 0;

  try {
    const value = globalThis.window.sessionStorage.getItem(getStorageKey(cartId));
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
};

const setRefreshCartImagesLastRefreshedAt = (cartId: string, lastRefreshedAt = Date.now()) => {
  if (!canUseSessionStorage()) return;

  try {
    globalThis.window.sessionStorage.setItem(getStorageKey(cartId), String(lastRefreshedAt));
  } catch {
    // Ignore storage write failures (private browsing, quota, etc.).
  }
};

const isRefreshCartImagesTtlActive = (lastRefreshedAt: number, now = Date.now(), ttlMs = REFRESH_CART_IMAGES_TTL_MS) =>
  now - lastRefreshedAt < ttlMs;

const sleep = (ms: number) => new Promise((resolve) => globalThis.window.setTimeout(resolve, ms));

const getStatusCode = (value: any): number | undefined => {
  const statusCode = Number(value?.statusCode ?? value?.status ?? value?.error?.statusCode ?? value?.error?.status);
  return Number.isFinite(statusCode) ? statusCode : undefined;
};

const isRetryableUpdateLineItemsError = (value: any): boolean => {
  const statusCode = getStatusCode(value);
  return [429, 502, 503, 504].includes(statusCode as number);
};

// Exponential backoff. The first retry will have a delay of 250ms, the second retry will have a delay of 500ms,
// the third retry will have a delay of 1000ms, and so on
const getRetryDelayMs = (retryCount: number) => {
  return UPDATE_LINE_ITEMS_BASE_DELAY_MS * Math.pow(2, retryCount - 1);
};

const shouldRefreshCartImages = ({
  cartId,
  lineItems = [],
  force,
}: ShouldRefreshCartImagesParams) => {
  if (!cartId) return false;
  if (
    !lineItems.some((lineItem) =>
      lineItemHasExpiredPhotos(lineItem),
    )
  )
    return false;

  if (force) return true;

  const lastRefreshedAt = getRefreshCartImagesLastRefreshedAt(cartId);
  return !isRefreshCartImagesTtlActive(lastRefreshedAt);
};

const useRefreshImages = () => {
  const { updateCachedCart, miniCarts, miniCartsLoaded } = useCart();
  const { projectSettings } = useProjectSettings();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const imageOptions = useMemo(() => setupImageOptions(projectSettings), [projectSettings?.MAX_PHOTO_FETCH_SIZE]);
  
  const { sendRefreshPhotos, subscribeToHostMessages } = useMessagePort();
  const currentRefreshCartImagesResultsPromise = useRef<Promise<RefreshImagesForCartResult> | null>(null);
  const currentRefreshSessionImagesPromise = useRef<Promise<OriginalPhotoInfo[]> | null>(null);

  /**
   *
   * @returns A promise that will resolve with an array of refreshed photos when
    *   the host site sends a valid fileSelection message.
    * @throws When the timeout is reached or the host sends an invalid fileSelection message.
   */
  const waitForFileSelection = useCallback((timeoutMs = FILE_SELECTION_TIMEOUT_MS) =>
    new Promise<OriginalPhotoInfo[]>((resolve, reject) => {
      let settled = false;
  
  
      const complete = () => {
        if (settled) return;
        console.warn(`did not receive a fileSelection event within ${timeoutMs} ms of sending the refreshPhotos message`);
        settled = true;
        cleanup();
        reject(new Error('Did not receive a fileSelection event within the timeout period'));
      };
  
      const onMessage = (event: MessageEvent) => {
        const { type, action, files } = event.data;
        if (type === 'fileSelection' && action === 'refreshPhotos') {
          if (settled) return;
          settled = true;
          cleanup();

          if (Array.isArray(files)) {
            resolve(files);
          } else {
            reject(new Error('Received invalid fileSelection message from the host application: ' + JSON.stringify(event.data)));
          }
        }
      };

      const unsubscribe = subscribeToHostMessages(onMessage);
      
      const cleanup = () => {
        unsubscribe();
        globalThis.window.clearTimeout(timeoutId);
      };
  
      const timeoutId = globalThis.window.setTimeout(complete, timeoutMs);
    }), [subscribeToHostMessages]);

  const updateLineItemsWithRetry = useCallback(
    async (payload: {
      cartId: string;
      distributionChannelKey: string;
      projectThumbnailUrl?: string;
      cartItems: LineItem[];
    }) => {
      let retryCount = 0;

      while (true) {
        try {
          const res = await sdk.composableCommerce.cart.updateLineItems(payload);

          if (!res?.isError) {
            return res;
          }

          const canRetry = isRetryableUpdateLineItemsError(res) && retryCount < UPDATE_LINE_ITEMS_MAX_RETRIES;
          if (!canRetry) {
            return res;
          }
        } catch (error) {
          const canRetry = retryCount < UPDATE_LINE_ITEMS_MAX_RETRIES;
          if (!canRetry) {
            return {
              isError: true,
              error,
            } as any;
          }
        }

        retryCount += 1;
        await sleep(getRetryDelayMs(retryCount));
      }
    },
    [],
  );

  
  const requestRefreshPhotos = useCallback(async (
    photoIds: string[],
    sendRefreshPhotos: (photoIds: string[]) => void,
  ): Promise<OriginalPhotoInfo[]> => {
    const uniquePhotoIds = Array.from(new Set(photoIds)).filter(Boolean);
  
    if (uniquePhotoIds.length === 0) {
      return [];
    }
  
    // If we've already sent a refresh photos request and are waiting
    // for a response to that one, we need to wait for that one to complete
    // before sending a new refresh photos request
    while (refreshPhotosPromise) {
      await refreshPhotosPromise.catch(() => []);
    }
  
    refreshPhotosPromise = (async () => {
      const waitForFileSelectionPromise = waitForFileSelection();
  
      sendRefreshPhotos(uniquePhotoIds);
  
      return waitForFileSelectionPromise;
    })();
  
    try {
      return await refreshPhotosPromise;
    } finally {
      refreshPhotosPromise = null;
    }
  }, [waitForFileSelection]);

  const updateCartsWithNewFiles = useCallback(
    async (carts: Cart[], files: OriginalPhotoInfo[]) => {
      if (!Array.isArray(files) || files.length === 0) {
        console.warn('fileSelection event received with no files');
        return [];
      }

      const filesById = new Map(files.map((file) => [file.id, file]));

      const refreshedCarts = await Promise.all(
        carts.map(async (cart) => {
          let projectThumbnailUrl: string | undefined;
          const lineItems = cart.lineItems ?? [];
          if (lineItems.length === 0) return cart;

          let hasUpdatedLineItem = false;
          const refreshedLineItems = lineItems.flatMap((lineItem) => {
            if (!lineItem.imageMetadata?.id) {
              return [];
            }
            const file = filesById.get(lineItem.imageMetadata.id);

            if (!file) {
              return [];
            }
            hasUpdatedLineItem = true;

            // If the projectThumbnailUrl is not already set for this cart, use the first file's
            // thumbnail as the projectThumbnailUrl
            if (!projectThumbnailUrl) {
              projectThumbnailUrl = getBuiltUrl(file, imageOptions, 200);
            }

            // Update the existing line item in the cart with the new image metadata
            // and also send it back so we can send the update to the backend
            lineItem.productId = lineItem.variant?.sku;
            lineItem.imageMetadata = {
              ...lineItem.imageMetadata,
              url: getBuiltUrl(file, imageOptions, Math.max(file.width, file.height)),
              projectThumbnailUrl,
              urlMidRes: getBuiltUrl(file, imageOptions, 700),
              urlThumb: getBuiltUrl(file, imageOptions, 200),
              expirationDate: file.expirationDate,
            };

            return [lineItem];
          });

          const distributionChannelKey = refreshedLineItems[0]?.distributionChannel?.key;
          if (hasUpdatedLineItem && cart.cartId && distributionChannelKey) {
            // Because the below update call can take a while, update the
            // cached cart in memory first, so that the images refresh sooner
            // Then after calling the backend to update the cart, update the cached
            // cart again with the response from the backend
            updateCachedCart(cart.cartId, { data: cart });
            const res = await updateLineItemsWithRetry({
              cartId: cart.cartId,
              distributionChannelKey,
              projectThumbnailUrl,
              cartItems: refreshedLineItems,
            });
            if (!res.isError) {
              setRefreshCartImagesLastRefreshedAt(cart.cartId, Date.now());
              updateCachedCart(cart.cartId, { data: res.data });
              return res.data;
            } else {
              throw new Error('Error updating cart with refreshed line items', { cause: res });
            }
          }

          return cart;
        }),
      );
      return refreshedCarts;
    },
    [updateCachedCart, updateLineItemsWithRetry, imageOptions],
  );

  const refreshImagesForCarts = useCallback(
    async (force?: boolean, cartArg?: Cart): Promise<RefreshImagesForCartResult> => {
      console.info('[refreshImagesForCarts] Refreshing images for carts');
      /*
       * Validation for a supplied cart does not depend on mini carts.
       *
       * Only skip when no cart was supplied and mini carts have not
       * finished loading.
       */
      if (!cartArg && !miniCartsLoaded) {
        console.info('[refreshImagesForCarts] Mini carts are still loading.');

        return {
          files: [],
          updatedCarts: [],
        };
      }

      if (currentRefreshCartImagesResultsPromise.current) {
        return currentRefreshCartImagesResultsPromise.current;
      }

      const refreshPromise = (async () => {
        const allCarts = cartArg ? [cartArg] : miniCarts;

        if (!Array.isArray(allCarts) || allCarts.length === 0) {
          console.info('[refreshImagesForCarts] No carts available for refreshing.');
          return {
            files: [],
            updatedCarts: [],
          };
        }

        const cartsToRefresh = allCarts.filter((cart) =>
          shouldRefreshCartImages({
            cartId: cart?.cartId,
            lineItems: cart?.lineItems,
            force,
          }),
        );

        if (cartsToRefresh.length === 0) {
          console.info('[refreshImagesForCarts] No carts need refreshing.');
          return {
            files: [],
            updatedCarts: [],
          };
        }

        const preselectedPhotoIds = getPreselectedPhotoIds(cartsToRefresh);

        if (preselectedPhotoIds.length === 0) {
          console.info('[refreshImagesForCarts] No images in any cart need refreshing');
          return {
            files: [],
            updatedCarts: [],
          };
        }

        const files = await requestRefreshPhotos(preselectedPhotoIds, sendRefreshPhotos);

        if (files.length === 0) {
          throw new Error('Host application returned no files for the requested photos');
        }

        const updatedCarts = await updateCartsWithNewFiles(cartsToRefresh, files);

        console.info('[refreshImagesForCarts] Successfully refreshed images for carts.');

        return {
          files,
          updatedCarts,
        };
      })();

      currentRefreshCartImagesResultsPromise.current = refreshPromise;

      try {
        return await refreshPromise;
      } finally {
        if (currentRefreshCartImagesResultsPromise.current === refreshPromise) {
          currentRefreshCartImagesResultsPromise.current = null;
        }
      }
    },
    [
      miniCarts,
      miniCartsLoaded,
      requestRefreshPhotos,
      sendRefreshPhotos,
      updateCartsWithNewFiles,
    ],
  );

  const refreshImagesForOrders = useCallback(
    async (
      lineItems: LineItem[] = [],
      options: RefreshImagesForOrdersOptions = {},
    ): Promise<RefreshImagesForOrdersResult> => {
      const { force } = options;

      if (!Array.isArray(lineItems) || lineItems.length === 0) {
        return {
          files: [],
          refreshedLineItems: [],
          refreshedPhotoIds: [],
        };
      }

      const lineItemsToRefresh = lineItems.filter((lineItem) => {
        if (force) {
          return lineItemHasPhoto(lineItem);
        }

        return lineItemHasExpiredPhotos(lineItem);
      });

      const preselectedPhotoIds = getPreselectedPhotoIdsFromLineItems(lineItemsToRefresh);

      if (preselectedPhotoIds.length === 0) {
        return {
          files: [],
          refreshedLineItems: lineItems,
          refreshedPhotoIds: [],
        };
      }

      const files = await requestRefreshPhotos(preselectedPhotoIds, sendRefreshPhotos);

      if (!Array.isArray(files) || files.length === 0) {
        return {
          files: [],
          refreshedLineItems: lineItems,
          refreshedPhotoIds: [],
        };
      }

      const filesById = new Map(
        files
          .filter((file) => typeof file.id === 'string' && file.id.length > 0)
          .map((file) => [file.id as string, file]),
      );

      const refreshedLineItems = lineItems.map((lineItem) => {
        const lineItemImageMetaDataId = lineItem.imageMetadata?.id;

        if (!lineItemImageMetaDataId) {
          return lineItem;
        }

        const file = filesById.get(lineItemImageMetaDataId);

        if (!lineItem.imageMetadata || !file) {
          return lineItem;
        }

        return {
          ...lineItem,
          imageMetadata: {
            ...lineItem.imageMetadata,
            url: getBuiltUrl(file, imageOptions, Math.max(file.width, file.height)),
            projectThumbnailUrl: getBuiltUrl(file, imageOptions, 200),
            urlMidRes: getBuiltUrl(file, imageOptions, 700),
            urlThumb: getBuiltUrl(file, imageOptions, 200),
            expirationDate: file.expirationDate,
          },
        };
      });
      const refreshedPhotoIds = files
        .map((file) => file.id)
        .filter((id): id is string => typeof id === 'string' && id.length > 0);

      return {
        files,
        refreshedLineItems,
        refreshedPhotoIds,
      };
    },
    [requestRefreshPhotos, sendRefreshPhotos, imageOptions],
  );

  /**
   * Re-fetches fresh file data (URLs, dimensions, expiration) from the host site for an explicit
   * set of photo IDs, without touching the cart backend or the 15-minute TTL throttle. Used to
   * rehydrate photos that are referenced by a cart/draft but no longer present in session storage
   * (e.g. session storage was cleared, then the user logged back in).
   */
  const refreshImagesByIds = useCallback(
    (photoIds: string[]): Promise<OriginalPhotoInfo[]> => requestRefreshPhotos(photoIds, sendRefreshPhotos),
    [requestRefreshPhotos, sendRefreshPhotos],
  );

  /** Refreshes expired photo metadata in customizationData and returns the current session photos. */
  const refreshImagesForSession = useCallback(async (): Promise<OriginalPhotoInfo[]> => {
    if (currentRefreshSessionImagesPromise.current) {
      return currentRefreshSessionImagesPromise.current;
    }

    const refreshPromise = (async () => {
      console.info('[refreshImagesForSession] Refreshing images for session')
      const customizationData = getCustomizationData<{ photoInfos?: OriginalPhotoInfo[] }>();
      const photoInfos = Array.isArray(customizationData.photoInfos) ? customizationData.photoInfos : [];
      const expiredPhotoIds = photoInfos
        .filter((photoInfo) => {
          const expirationTime = photoInfo.expirationDate ? new Date(photoInfo.expirationDate).getTime() : undefined;
          return photoInfo.id && (!expirationTime || expirationTime < Date.now());
        })
        .map((photoInfo) => photoInfo.id);

      if (expiredPhotoIds.length === 0) {
        console.info('[refreshImagesForSession] No expired photos found in session');
        return photoInfos;
      }

      const refreshedPhotos = await requestRefreshPhotos(expiredPhotoIds, sendRefreshPhotos);
      const refreshedPhotosById = new Map(refreshedPhotos.filter((photo) => photo?.id).map((photo) => [photo.id, photo]));

      const updatedCustomizationData = updateCustomizationData((current) => {
        const currentPhotoInfos = Array.isArray(current.photoInfos) ? current.photoInfos : [];

        return {
          ...current,
          photoInfos: currentPhotoInfos.map((photo: OriginalPhotoInfo) =>
            refreshedPhotosById.has(photo.id) ? { ...photo, ...refreshedPhotosById.get(photo.id) } : photo,
          ),
        };
      });
      console.info('[refreshImagesForSession] Successfully refreshed images for session');

      return updatedCustomizationData.photoInfos;
    })();

    currentRefreshSessionImagesPromise.current = refreshPromise;

    try {
      return await refreshPromise;
    } finally {
      if (currentRefreshSessionImagesPromise.current === refreshPromise) {
        currentRefreshSessionImagesPromise.current = null;
      }
    }
  }, [requestRefreshPhotos, sendRefreshPhotos]);

  return {
    refreshImagesForCarts,
    refreshImagesForOrders,
    refreshImagesForSession,
    refreshImagesByIds,
    miniCartsLoaded,
  };
};

export default useRefreshImages;
